import { ReactNode, useLayoutEffect, useRef, useState } from "react";
import { buildClasses } from "../utils/buildClasses";
import { RYE_SLIDE_EASING, RYE_SLIDE_MS } from "./motion";

/** Animate content changes using measured heights, including changes to/from auto. */
export function RyeAnimatedHeight({ children }: { children: ReactNode }) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>();

  useLayoutEffect(() => {
    const content = contentRef.current;
    if (!content || typeof ResizeObserver === "undefined") return;
    // Measure layout size, not getBoundingClientRect(): that includes
    // transforms, so content inside a scaling-in modal would measure short and
    // stay clipped (a transform alone doesn't fire the observer to correct it).
    // The observer reports the initial size before first paint.
    const observer = new ResizeObserver(([entry]) =>
      setHeight(entry.borderBoxSize?.[0]?.blockSize ?? content.offsetHeight),
    );
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      style={{
        height,
        transitionDuration: `${RYE_SLIDE_MS}ms`,
        transitionTimingFunction: RYE_SLIDE_EASING,
      }}
      className={buildClasses(
        "overflow-hidden",
        "transition-[height]",
        "motion-reduce:transition-none",
      )}
    >
      <div ref={contentRef} className={buildClasses("flow-root")}>
        {children}
      </div>
    </div>
  );
}
